import { Button } from '@neo/index'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Select } from '@neo/primitives/select'
import { Plus, Trash2 } from 'lucide-react'
import { v4 } from 'uuid'
import { NodevarList } from '../nodevar-list'

const isValidUrl = (url: string) => {
  if (!url) return true
  if (/^\{\{[a-zA-Z_][a-zA-Z0-9_.[\]-]*\}\}$/.test(url)) return true
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
  } catch {
    return false
  }
}

export type HttpMethod = 'GET' | 'POST'

export interface EventCallback {
  id: string
  event_type: string
  url: string
  method: HttpMethod
}

export interface EventCallbacksProps {
  events: EventCallback[]
  onChange: (events: EventCallback[]) => void
  eventTypes: { value: string; label: string }[]
  nodeVars?: string[]
  className?: string
}

const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
]

export const EventCallbacks = ({
  events,
  onChange,
  eventTypes,
  nodeVars = [],
  className,
}: EventCallbacksProps) => {
  const handleAddEvent = () => {
    // Default the new row to the first event type not already used, so adding
    // multiple rows doesn't create duplicates (the BE keys callbacks by event
    // type — one per type per node). Falls back to the first type if all used.
    const usedTypes = new Set(events.map((e) => e.event_type))
    const nextType =
      eventTypes.find((et) => !usedTypes.has(et.value))?.value ?? eventTypes[0]?.value
    onChange([
      ...events,
      {
        id: v4(),
        event_type: nextType ?? '',
        url: '',
        method: 'POST',
      },
    ])
  }

  const handleRemoveEvent = (index: number) => {
    const newEvents = events.filter((_, i) => i !== index)
    onChange(newEvents)
  }

  const handleEventChange = (index: number, field: keyof EventCallback, value: string) => {
    const newEvents = events.map((event, i) => {
      if (i === index) {
        return { ...event, [field]: value }
      }
      return event
    })
    onChange(newEvents)
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <Label className="text-h4-500 text-foreground">Event callbacks</Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAddEvent}
          type="button"
          disabled={events.length >= eventTypes.length}
          data-testid="button-add-event"
        >
          <Plus size={16} className="mr-1" />
          Add event
        </Button>
      </div>

      {events.length === 0 ? (
        <div className="font-inter text-s-400 text-secondary text-center py-4 border border-border rounded-md border-dashed">
          No event callbacks configured
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event, index) => (
            <div key={event.id} className="p-3 border border-border bg-card rounded-md space-y-2">
              <div className="flex justify-between items-start">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="mb-1 block text-xs-400">Event type</Label>
                    <Select
                      value={event.event_type}
                      onChange={(value) => handleEventChange(index, 'event_type', value as string)}
                      options={eventTypes.filter(
                        (et) =>
                          et.value === event.event_type ||
                          !events.some((e, i) => i !== index && e.event_type === et.value),
                      )}
                      placeholder="Select event"
                      data-testid="select-event-type"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs-400">Method</Label>
                    <Select
                      value={event.method}
                      onChange={(value) => handleEventChange(index, 'method', value as string)}
                      options={httpMethods}
                      placeholder="Select method"
                      data-testid="select-event-method"
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveEvent(index)}
                  className="ml-2"
                  type="button"
                  data-testid="button-remove-event"
                >
                  <Trash2 size={16} className="text-destructive" />
                </Button>
              </div>
              <div>
                <Label className="mb-1 block text-xs-400">URL</Label>
                <Input
                  value={event.url}
                  onChange={(e) => handleEventChange(index, 'url', e.target.value)}
                  inputMode="url"
                  placeholder="https://example.com/webhook"
                  inputClassName="font-jetbrains text-xs-400"
                  className={event.url && !isValidUrl(event.url) ? 'border-destructive' : ''}
                  endIcon={
                    <NodevarList
                      nodevars={nodeVars}
                      className="pr-2"
                      inputElementSelector={`[data-event-url="${index}"]`}
                    />
                  }
                  data-event-url={index}
                  data-testid="input-event-url"
                />
                {event.url && !isValidUrl(event.url) && (
                  <p className="text-xs-400 text-destructive mt-1">
                    Please enter a valid HTTP(S) URL
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
